import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSkeleton } from '../DashboardLoader';
import {
  PiMagnifyingGlass,
  PiFunnelSimple,
  PiMapPin,
  PiPhone,
  PiEnvelopeSimple,
  PiEye,
  PiPlus,
  PiArrowClockwise,
  PiWarningCircle,
  PiProhibit,
  PiCheck,
  PiTrash,
  PiStorefront,
  PiSealCheck,
  PiFileText,
} from 'react-icons/pi';
import { useAdminRestaurants } from '../../../hooks/useAdminRestaurants';
import { pickRestaurantImageUrl } from '../../../utils/mediaUrl';
import { RatingStarsFa, getDisplayRating } from '../../../utils/ratingStars';
import RestaurantDocumentsModal from '../modals/RestaurantDocumentsModal';

const iconBtn =
  'inline-flex items-center justify-center rounded-xl bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-800 transition-colors';

const AdminRestaurants = () => {
  const navigate = useNavigate();
  const {
    restaurants,
    loading,
    error,
    searchTerm,
    filterStatus,
    currentPage,
    totalPages,
    suspendRestaurantById,
    activateRestaurantById,
    deleteRestaurantById,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    refetch,
  } = useAdminRestaurants();

  const [actionLoading, setActionLoading] = useState({});
  const [imgFailed, setImgFailed] = useState({});
  const [documentsRestaurant, setDocumentsRestaurant] = useState(null);

  const handleSuspend = async (restaurantId) => {
    if (
      !window.confirm(
        'Suspendre ce restaurant ? Il ne sera plus visible dans le catalogue visiteurs (recherche, accueil).'
      )
    ) {
      return;
    }
    const note = window.prompt(
      'Message optionnel pour les visiteurs qui ouvriraient un ancien lien (sinon laisser vide) :',
      ''
    );
    if (note === null) {
      return;
    }
    setActionLoading({ ...actionLoading, [restaurantId]: true });
    const result = await suspendRestaurantById(restaurantId, {
      suspension_note: note.trim() || undefined,
    });
    setActionLoading({ ...actionLoading, [restaurantId]: false });
    if (result.success) {
      alert('Restaurant suspendu avec succès');
    } else {
      alert(`Erreur: ${result.error}`);
    }
  };

  const handleActivate = async (restaurantId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir activer ce restaurant ?')) {
      return;
    }
    setActionLoading({ ...actionLoading, [restaurantId]: true });
    const result = await activateRestaurantById(restaurantId);
    setActionLoading({ ...actionLoading, [restaurantId]: false });
    if (result.success) {
      alert('Restaurant activé avec succès');
    } else {
      alert(`Erreur: ${result.error}`);
    }
  };

  const handleDelete = async (restaurantId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce restaurant ? Cette action est irréversible.')) {
      return;
    }
    setActionLoading({ ...actionLoading, [restaurantId]: true });
    const result = await deleteRestaurantById(restaurantId);
    setActionLoading({ ...actionLoading, [restaurantId]: false });
    if (result.success) {
      alert('Restaurant supprimé avec succès');
    } else {
      alert(`Erreur: ${result.error}`);
    }
  };

  const filteredRestaurants = restaurants.filter((restaurant) => {
    const matchesSearch =
      restaurant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(restaurant.owner || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(restaurant.cuisine || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || restaurant.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/80',
      pending: 'bg-amber-50 text-amber-900 ring-1 ring-amber-200/80',
      suspended: 'bg-rose-50 text-rose-900 ring-1 ring-rose-200/80',
      rejected: 'bg-stone-100 text-stone-700 ring-1 ring-stone-200/80',
    };

    const labels = {
      active: 'Actif',
      pending: 'En attente',
      suspended: 'Suspendu',
      rejected: 'Rejeté',
    };

    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${styles[status] || styles.pending}`}>
        {labels[status] || 'Inconnu'}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <PiWarningCircle className="w-5 h-5 text-red-500 mr-2 shrink-0" />
            <h2 className="text-lg font-semibold text-red-800">Erreur de chargement</h2>
          </div>
          <p className="text-red-600 mt-2">{error}</p>
          <button
            onClick={refetch}
            className="mt-3 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <PiArrowClockwise className="w-4 h-4 mr-2" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Restaurants</h1>
          <p className="text-gray-600">Gérez les restaurants de la plateforme</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/business/signup')}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-white rounded-xl hover:opacity-95 transition-opacity"
          style={{ backgroundColor: 'rgb(5, 70, 37)' }}
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
            <PiPlus className="w-5 h-5" />
          </span>
          Ajouter un restaurant
        </button>
      </div>

      <div className="bg-white p-4 rounded-xl mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <PiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-5 h-5 pointer-events-none" />
              <input
                type="text"
                placeholder="Rechercher un restaurant..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[rgb(5,70,37)]/25 focus:border-[rgb(5,70,37)]"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:min-w-[200px]">
            <span className={`${iconBtn} w-10 h-10 shrink-0`}>
              <PiFunnelSimple className="w-5 h-5" />
            </span>
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="flex-1 px-3 py-2.5 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[rgb(5,70,37)]/25"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRestaurants.map((restaurant) => {
          const ownerName =
            typeof restaurant.owner === 'object' ? restaurant.owner?.email || 'N/A' : restaurant.owner || 'N/A';
          const ownerEmail = restaurant.email || (typeof restaurant.owner === 'object' ? restaurant.owner?.email : 'N/A');
          const isLoading = actionLoading[restaurant.id];
          const displayRating = getDisplayRating(restaurant, 0);

          const imageUrl = pickRestaurantImageUrl(restaurant);
          const showImage = imageUrl && !imgFailed[restaurant.id];

          return (
            <div
              key={restaurant.id}
              className="bg-white rounded-2xl overflow-hidden transition-shadow ring-1 ring-stone-100"
            >
              <div className="h-48 bg-gradient-to-br from-stone-100 to-stone-200 relative overflow-hidden">
                {showImage ? (
                  <img
                    src={imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => setImgFailed((f) => ({ ...f, [restaurant.id]: true }))}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-400 gap-2">
                    <PiStorefront className="w-14 h-14 opacity-60" />
                    <span className="text-xs font-medium">Aucune image</span>
                  </div>
                )}
                <div className="absolute top-3 right-3 flex items-center gap-1.5">
                  {restaurant.is_certified && (
                    <span
                      title="Restaurant certifié"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/80"
                    >
                      <PiSealCheck className="w-3.5 h-3.5" /> Certifié
                    </span>
                  )}
                  {getStatusBadge(restaurant.status)}
                </div>
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-lg">
                    <RatingStarsFa rating={displayRating} size="sm" />
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">
                      {displayRating > 0 ? displayRating.toFixed(1) : '—'}
                    </span>
                  </div>
                  <div className="bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-lg text-xs text-gray-600">
                    {restaurant.reviews || 0} avis
                  </div>
                </div>
              </div>

              <div className="p-5">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{restaurant.name}</h3>
                  <p className="text-sm text-gray-600">{restaurant.cuisine || 'Non spécifié'}</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-1">
                    <PiMapPin className="w-4 h-4 text-stone-400 shrink-0" />
                    {restaurant.address || 'Non spécifié'}
                  </p>
                </div>

                <div className="mb-4 space-y-1">
                  <p className="text-sm font-medium text-gray-900">Propriétaire : {ownerName}</p>
                  <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    <PiEnvelopeSimple className="w-4 h-4 text-stone-400 shrink-0" />
                    {ownerEmail}
                  </div>
                  {restaurant.phone && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                      <PiPhone className="w-4 h-4 text-stone-400 shrink-0" />
                      {restaurant.phone}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="text-center rounded-xl bg-stone-50 py-3">
                    <p className="text-2xl font-bold text-gray-900">{restaurant.orders || 0}</p>
                    <p className="text-xs text-gray-500">Commandes</p>
                  </div>
                  <div className="text-center rounded-xl bg-stone-50 py-3">
                    <p className="text-2xl font-bold text-gray-900">{restaurant.revenue?.toFixed(2) || '0.00'}€</p>
                    <p className="text-xs text-gray-500">Revenus</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      title="Voir la fiche publique"
                      onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                      className={`${iconBtn} w-10 h-10`}
                    >
                      <PiEye className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      title="Documents de certification"
                      onClick={() => setDocumentsRestaurant(restaurant)}
                      className={`${iconBtn} w-10 h-10`}
                    >
                      <PiFileText className="w-5 h-5" />
                    </button>
                    {restaurant.status === 'active' ? (
                      <button
                        type="button"
                        onClick={() => handleSuspend(restaurant.id)}
                        disabled={isLoading}
                        className={`${iconBtn} w-10 h-10 disabled:opacity-50`}
                        title="Suspendre"
                      >
                        {isLoading ? (
                          <PiArrowClockwise className="w-5 h-5 animate-spin" />
                        ) : (
                          <PiProhibit className="w-5 h-5" />
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleActivate(restaurant.id)}
                        disabled={isLoading}
                        className={`${iconBtn} w-10 h-10 disabled:opacity-50`}
                        title="Activer"
                      >
                        {isLoading ? (
                          <PiArrowClockwise className="w-5 h-5 animate-spin" />
                        ) : (
                          <PiCheck className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(restaurant.id)}
                      disabled={isLoading}
                      className={`${iconBtn} w-10 h-10 hover:bg-rose-100 hover:text-rose-700 disabled:opacity-50`}
                      title="Supprimer"
                    >
                      {isLoading ? (
                        <PiArrowClockwise className="w-5 h-5 animate-spin" />
                      ) : (
                        <PiTrash className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredRestaurants.length === 0 && !loading && (
        <div className="text-center py-12">
          <PiStorefront className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucun restaurant trouvé</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-gray-700">
            Affichage de {(currentPage - 1) * 20 + 1} à {Math.min(currentPage * 20, filteredRestaurants.length)} sur{' '}
            {filteredRestaurants.length} résultats
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-stone-200 rounded-xl hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <span className="px-3 py-2 text-sm font-medium text-gray-700">
              Page {currentPage} sur {totalPages}
            </span>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-stone-200 rounded-xl hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      <RestaurantDocumentsModal
        isOpen={Boolean(documentsRestaurant)}
        restaurant={documentsRestaurant}
        onClose={() => setDocumentsRestaurant(null)}
        onCertificationChange={refetch}
      />
    </div>
  );
};

export default AdminRestaurants;
